import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  changeClientPortalPassword,
  clearClientPortalToken,
  getClientPortalMe,
  getClientPortalToken,
} from '../api/clientPortalApi';
import InlineLoading from '../components/InlineLoading';

const ClientPortalPasswordPage = () => {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const loadClient = async () => {
      if (!getClientPortalToken()) {
        navigate('/', { replace: true });
        return;
      }
      setLoading(true);
      try {
        const data = await getClientPortalMe();
        if (mounted) setClient(data);
      } catch (requestError) {
        clearClientPortalToken();
        navigate('/', { replace: true });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadClient();
    return () => {
      mounted = false;
    };
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    if (newPassword.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }
    setSaving(true);
    try {
      const response = await changeClientPortalPassword({
        password: currentPassword,
        newPassword,
      });
      setClient(response?.client || client);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMessage('Contraseña actualizada.');
      navigate('/client-portal/purchase-orders/new', { replace: true });
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'No se pudo cambiar la contraseña.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    clearClientPortalToken();
    navigate('/', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <InlineLoading label="Cargando perfil" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-lg shadow p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {client?.mustChangePassword ? 'Cambia tu contraseña' : 'Actualizar contraseña'}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {client?.name || 'Cliente'} · {client?.rut || ''}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            placeholder="Contraseña actual"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            required
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="password"
            placeholder="Nueva contraseña"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            required
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="password"
            placeholder="Confirmar nueva contraseña"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            disabled={saving}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? 'Guardando...' : 'Guardar contraseña'}
          </button>
        </form>
        {message && <p className="mt-4 text-sm text-emerald-700">{message}</p>}
        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
        <button
          type="button"
          onClick={handleLogout}
          className="mt-4 w-full bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300"
        >
          Salir
        </button>
      </div>
    </div>
  );
};

export default ClientPortalPasswordPage;
